import Logo from '@avanta/shared/assets/avanta-logo.svg';
import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router';
import { Breadcrumb } from 'components/ui/Breadcrumb';
import { ROUTES } from 'constants/routes';
import { WellbeingTypes } from 'constants/wellbeing';
import { useWellbeingContent } from 'hooks/useWellbeingContent';
import { STYLES } from './styles';
import { renderContent } from './utils';

export const ArticlePage = (): React.ReactElement => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);

  const { data: articles, isLoading: isLoadingArticles } = useWellbeingContent(
    WellbeingTypes.articles
  );
  const { data: exercises, isLoading: isLoadingExercises } = useWellbeingContent(
    WellbeingTypes.exercises
  );
  const content = [...(articles ?? []), ...(exercises ?? [])];
  const articleData = content?.find(item => item.id === id);
  const isLoading = isLoadingArticles || isLoadingExercises;

  useEffect(() => {
    if (!isLoading && !articleData) {
      navigate(ROUTES.WELL_BEING);
    }
  }, [articleData, isLoading, navigate]);

  if (isLoading) return <LoadingScreen />;

  const isAnExercise = articleData?.isAnExercise;
  const breadcrumbLinks = [
    { href: ROUTES.WELL_BEING, label: t('Nav.wellBeing', { ns: LOCIZE_NS.COMMON }) },
    {
      href: isAnExercise ? `${ROUTES.WELL_BEING}/exercises` : `${ROUTES.WELL_BEING}/articles`,
      label: isAnExercise
        ? t('WellBeingContent.exercises.heading')
        : t('WellBeingContent.articles.heading'),
    },
    {
      href: isAnExercise
        ? `${ROUTES.WELL_BEING}/exercises/${id}`
        : `${ROUTES.WELL_BEING}/articles/${id}`,
      label: articleData?.title ?? '',
    },
  ];

  return (
    <div className={STYLES.CONTAINER}>
      <Breadcrumb links={breadcrumbLinks} />
      <div className={STYLES.INNER_CONTAINER}>
        <Card className="overflow-hidden">
          {articleData?.imageURL && (
            <div className={STYLES.HERO_IMAGE}>
              <img src={articleData.imageURL} alt={articleData?.title} className={STYLES.IMAGE} />
            </div>
          )}
          <div className={STYLES.CONTENT_WRAPPER}>
            <div className="flex flex-col gap-6">
              <h1 className={STYLES.TITLE}>{articleData?.title ?? ''}</h1>
              {articleData?.description && (
                <p className={STYLES.BRIEF_DESC}>{articleData.description}</p>
              )}
            </div>
            <div className={STYLES.PROSE}>
              {articleData?.content?.map((contentItem, index) => renderContent(contentItem, index))}
            </div>
            <div className={STYLES.FOOTER}>
              <div className="text-center">
                <img src={Logo} alt="AvantaMynd" className={STYLES.LOGO} />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
